/**
 * Derives the Users-listing Subscription badge from the RevenueCat state that
 * the webhook already writes onto each user document.
 *
 * NOTHING HERE COSTS A READ. `subscribeToUsers` already streams the whole user
 * doc; these fields were simply absent from its field whitelist. The webhook
 * (watchlock `functions/src/revenueCatWebhook.ts`) is the sole writer of every
 * field below — all of them are server-owned and locked against client writes.
 *
 * The `plan` column already carries the tier name, so this badge deliberately
 * shows STATE ONLY ("Trial 13d", "Cancelled 21d", "Lapsed") rather than
 * repeating "Curator" twice on the same row.
 *
 * Two quirks of the live webhook are load-bearing here:
 *
 *  1. There is no 'cancelled' subscription state. CANCELLATION stamps
 *     `cancelledAt` and leaves `state` at 'trial'/'active', because the user
 *     keeps access until the period ends. Cancellation is therefore derived
 *     from `cancelledAt`, never from `state`.
 *
 *  2. `subscription.billingIssue` is currently NEVER CLEARED — handleBillingIssue
 *     sets it true, and the recovery RENEWAL sets state back to 'active' without
 *     unsetting the flag. So a bare `billingIssue === true` cannot be trusted to
 *     mean "billing is broken right now". We key the ⚠ badge on `state === 'grace'`
 *     (which the webhook does maintain correctly) and demote a stale flag to a
 *     tooltip note. PR4 fixes the webhook; when it lands, the tooltip note stops
 *     appearing on healthy accounts on its own, with no change needed here.
 */

import type { UserSubscriptionSnapshot } from '../../types';

/** Filter buckets, and the identity of each badge. */
export type SubscriptionKind =
  | 'none'
  | 'trial'
  | 'trialCancelled'
  | 'trialExpired'
  | 'active'
  | 'cancelled'
  | 'billing'
  | 'refunded'
  | 'lapsed';

export interface SubscriptionBadge {
  kind: SubscriptionKind;
  label: string;
  variant: 'default' | 'success' | 'warning' | 'danger' | 'info' | 'amber';
  /** Tooltip: the full story, including anything the short label had to drop. */
  title: string;
  /**
   * A RevenueCat SANDBOX purchase (TestFlight / simulator). Rendered as a ⌂
   * suffix — without it a sandbox trial is visually identical to real revenue.
   */
  sandbox: boolean;
}

/**
 * The snapshot with its timestamp fields loosened to `unknown`.
 *
 * Two reasons, both real: a Firestore `Timestamp` is only one of the shapes
 * these arrive in (a doc written by the Admin SDK and read back before the
 * server value lands can surface `{seconds}`), and it keeps the derivation
 * testable with plain objects instead of constructed Timestamps. `toDateSafe`
 * narrows them all. The strict `UserSubscriptionSnapshot` is assignable to
 * this, so a real `FirestoreUser` passes without a cast.
 */
type LooseSubscription = Omit<
  UserSubscriptionSnapshot,
  'expiresAt' | 'cancelledAt' | 'billingIssueAt' | 'monthlyPaidSince'
> & {
  expiresAt?: unknown;
  cancelledAt?: unknown;
  billingIssueAt?: unknown;
  monthlyPaidSince?: unknown;
};

/** The subset of a user doc this derivation reads. */
export interface SubscriptionInput {
  plan?: string;
  trialTier?: string;
  trialEndsAt?: unknown;
  hasEverBeenPaid?: boolean;
  subscription?: LooseSubscription;
}

/**
 * RevenueCat's cancel reason for a support-initiated refund. The other values
 * ('UNSUBSCRIBE', 'BILLING_ERROR', 'DEVELOPER_INITIATED', …) are ordinary
 * cancellations and are surfaced through the tooltip rather than the label.
 */
const REFUND_CANCEL_REASON = 'CUSTOMER_SUPPORT';

/**
 * Normalise the several shapes a Firestore timestamp arrives in (Timestamp,
 * {seconds}, Date, ISO string) to a Date. Returns null for anything else,
 * including the `undefined` that every optional field can be.
 */
export function toDateSafe(value: unknown): Date | null {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === 'object') {
    const asTimestamp = value as { toDate?: unknown; seconds?: unknown };
    if (typeof asTimestamp.toDate === 'function') {
      const d = (value as { toDate: () => Date }).toDate();
      return d instanceof Date && !Number.isNaN(d.getTime()) ? d : null;
    }
    if (typeof asTimestamp.seconds === 'number') {
      return new Date(asTimestamp.seconds * 1000);
    }
  }
  if (typeof value === 'string') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
}

/**
 * Whole days from `now` until `value`, or null if there's no usable date.
 * Already-past dates clamp to 0 rather than going negative — "ends in -3d"
 * reads as a bug, and the state badge already says the period has lapsed.
 */
export function daysUntil(value: unknown, now: Date): number | null {
  const date = toDateSafe(value);
  if (!date) return null;
  const ms = date.getTime() - now.getTime();
  if (ms <= 0) return 0;
  return Math.ceil(ms / 86_400_000);
}

/** " 13d" / "" — the remaining-days suffix, omitted when we can't compute one. */
function remainingSuffix(days: number | null): string {
  return days === null ? '' : ` ${days}d`;
}

function describeExpiry(days: number | null, date: Date | null): string {
  if (date === null) return 'end date unknown';
  if (days === null) return 'end date unknown';
  if (days === 0) return `ended ${date.toLocaleDateString()}`;
  return `until ${date.toLocaleDateString()} (${days}d)`;
}

/**
 * Derive the badge for one user. Returns null when there is nothing to show —
 * a free user who has never had any RevenueCat activity — which the table
 * renders as a muted dash.
 *
 * `now` is injected so the day arithmetic is testable without freezing time.
 */
export function deriveSubscriptionBadge(
  user: SubscriptionInput,
  now: Date = new Date(),
): SubscriptionBadge | null {
  const sub = user.subscription;
  const state = sub?.state;

  // A user the webhook has never touched. `plan` alone is not evidence of a
  // subscription — it defaults to 'free' in the mapper for every user.
  if (!sub || (!state && !sub.lastEventType)) return null;

  const sandbox = sub.environment === 'SANDBOX';
  const cancelledAt = toDateSafe(sub.cancelledAt);
  const isRefund = sub.cancelReason === REFUND_CANCEL_REASON;

  // A stale billing flag is common until the webhook learns to clear it, so it
  // only ever annotates — it never drives the badge on its own.
  const staleBillingNote =
    sub.billingIssue === true && state !== 'grace'
      ? ' · a past billing issue is recorded on this account'
      : '';

  const sandboxNote = sandbox
    ? ' · RevenueCat SANDBOX purchase, not real revenue'
    : '';

  const suffix = `${staleBillingNote}${sandboxNote}`;

  // ── Refund ────────────────────────────────────────────────────────────────
  // Checked before everything else: a support-initiated cancellation is the one
  // case where the reason matters more than the lifecycle position.
  if (isRefund) {
    return {
      kind: 'refunded',
      label: 'Refunded',
      variant: 'danger',
      title: `Cancelled by customer support (refund)${suffix}`,
      sandbox,
    };
  }

  // ── Billing problem, right now ────────────────────────────────────────────
  if (state === 'grace') {
    return {
      kind: 'billing',
      label: '⚠ Billing',
      variant: 'danger',
      title: `Payment failed — in the store's grace period; RevenueCat will send EXPIRATION if it goes unresolved${sandboxNote}`,
      sandbox,
    };
  }

  // ── Trial ─────────────────────────────────────────────────────────────────
  if (state === 'trial') {
    const endsAt = toDateSafe(user.trialEndsAt) ?? toDateSafe(sub.expiresAt);
    const days = daysUntil(endsAt, now);
    if (cancelledAt) {
      return {
        kind: 'trialCancelled',
        label: `Trial ✕${remainingSuffix(days)}`,
        variant: 'warning',
        title: `Trial cancelled ${cancelledAt.toLocaleDateString()} — will not convert; access ${describeExpiry(days, endsAt)}${suffix}`,
        sandbox,
      };
    }
    return {
      kind: 'trial',
      label: `Trial${remainingSuffix(days)}`,
      variant: 'info',
      title: `On trial — access ${describeExpiry(days, endsAt)}${suffix}`,
      sandbox,
    };
  }

  // ── Paid ──────────────────────────────────────────────────────────────────
  if (state === 'active') {
    const endsAt = toDateSafe(sub.expiresAt);
    const days = daysUntil(endsAt, now);
    if (cancelledAt) {
      return {
        kind: 'cancelled',
        label: `Cancelled${remainingSuffix(days)}`,
        variant: 'warning',
        title: `Cancelled ${cancelledAt.toLocaleDateString()}${
          sub.cancelReason ? ` (${sub.cancelReason})` : ''
        } — keeps access ${describeExpiry(days, endsAt)}${suffix}`,
        sandbox,
      };
    }
    return {
      kind: 'active',
      label: 'Active',
      variant: 'success',
      title: `Subscription active — renews ${describeExpiry(days, endsAt)}${suffix}`,
      sandbox,
    };
  }

  // ── Ended ─────────────────────────────────────────────────────────────────
  if (state === 'expired') {
    // A trial that ran out without ever converting. `trialTier` is the permanent
    // "consumed a trial" marker the eligibility gate reads, so it survives the
    // expiry — paired with `hasEverBeenPaid !== true` it identifies a trialist
    // who never paid, as opposed to a former subscriber.
    if (user.trialTier != null && user.hasEverBeenPaid !== true) {
      return {
        kind: 'trialExpired',
        label: 'Trial expired',
        variant: 'default',
        title: `Trial ended without converting — no longer eligible for another${suffix}`,
        sandbox,
      };
    }
    return {
      kind: 'lapsed',
      label: 'Lapsed',
      variant: 'default',
      title: `Subscription ended${
        cancelledAt ? ` (cancelled ${cancelledAt.toLocaleDateString()})` : ''
      }${suffix}`,
      sandbox,
    };
  }

  // `state === 'free'`, or an event type we have no state for yet. Say so
  // plainly rather than guessing a lifecycle position.
  return {
    kind: 'none',
    label: 'Free',
    variant: 'default',
    title: `No active subscription${
      sub.lastEventType ? ` · last RevenueCat event: ${sub.lastEventType}` : ''
    }${suffix}`,
    sandbox,
  };
}

/** Filter chips offered above the table, in display order. */
export const SUBSCRIPTION_FILTERS: Array<{
  key: SubscriptionKind;
  label: string;
}> = [
  { key: 'trial', label: 'On trial' },
  { key: 'trialCancelled', label: 'Trial cancelled' },
  { key: 'trialExpired', label: 'Trial expired' },
  { key: 'active', label: 'Subscribed' },
  { key: 'cancelled', label: 'Cancelled' },
  { key: 'billing', label: 'Billing issue' },
  { key: 'refunded', label: 'Refunded' },
  { key: 'lapsed', label: 'Lapsed' },
];
